'use client';
/**
 * Chantier manuel-vitrine, lots V2–V4 — les registres du référentiel des
 * analyses, en onglets de l'écran « Référentiel des analyses ».
 *
 *   · V2 — TECHNIQUES : les techniques d'un examen et leurs équipements. C'est
 *     la liaison équipements ↔ analyses du chantier : elle passe par la
 *     technique (un examen à deux techniques ne tourne pas sur les mêmes
 *     automates selon la technique).
 *   · V3 — INTERVALLES : stratifiés, datés, historisés. Un intervalle ne se
 *     MODIFIE pas : il se clôt et un autre s'ouvre — le moteur refuse le
 *     chevauchement de strates. La table naît vide : rien n'est adopté tant
 *     que le biologiste n'a pas validé les valeurs.
 *   · V3 — CRITÈRES : le référentiel des critères préanalytiques
 *     d'acceptation/refus, communs (liés à N fiches) ou propres. Modifier un
 *     critère commun montre l'IMPACT avant d'écrire (409 du moteur →
 *     confirmation explicite).
 *   · V4 — COMBOS : la somme de plusieurs examens prescrits ensemble
 *     (IgG + IgM). Un combo ne porte ni conditions ni intervalles : tout se
 *     lit sur ses membres. Brouillon → publication explicite.
 */
import React, { useEffect, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { I, card, EmptyState, Skeleton, ChampRecherche, BoutonCreer, Modale, inputStyle, TitreRubrique, styleRubrique, SelecteurPastilles, BoutonIcone, useConfirmation } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { trancheAgeLisible } from '@/shared/ageLisible';
import { LeveyJennings, type Bloc } from '@/components/screens/ressources/Cqi';
import { CriteresPourAnalyte, useCriteresPerformance } from './PerformanceFiche';
import type { GedCqiCarteResume } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { aujourdhuiIso, fmtDate } from '@/lib/meta';
type Ref = { documentId: string; code: string; nom?: string; libelle?: string };
export type FicheDetail = {
  documentId: string; code: string; libelle: string;
  techniques: {
    documentId: string; code: string; nom: string; principe: string | null;
    ordre: number; actif: boolean; equipements: Ref[];
    /** Lot G (17/08) : les analyses qui empruntent cette technique — la
     *  relation est devenue MULTIPLE, une technique est un savoir-faire du
     *  laboratoire, pas la propriété d'un examen. */
    fiches?: { documentId: string; code: string; libelle: string }[];
  }[];
  intervalles: {
    documentId: string; sexe: string; grossesse: string; condition: string | null;
    ageMin: number | null; ageMax: number | null;
    borneMin: number | null; borneMax: number | null; texteLibre: string | null;
    unite: string | null; source: string | null; commentaire: string | null;
    dateApplication: string; dateFin: string | null;
    technique: { documentId: string; code: string; nom: string } | null;
    analyte: { documentId: string; code: string; nom: string } | null;
    /** `parametre` = intervalle DÉRIVÉ d'un paramètre de l'examen, jamais
     *  recopié en base (25/08/2026). Absent = intervalle propre à l'examen.
     *  Un examen n'a pas de valeurs usuelles en propre : il en a parce que ses
     *  paramètres en ont — 176 intervalles n'étaient rattachés qu'au paramètre
     *  et n'apparaissaient sur aucun examen qui l'emploie. */
    origine?: 'parametre';
  }[];
  criteresEchantillon: {
    documentId: string; code: string; libelle: string; type: string;
    commun: boolean; actif: boolean;
  }[];
};

const rangee: React.CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 };
/* ⚠️ `grid` et non `block` : l'intitulé était suivi d'un `<br />`, dont
   l'écart est l'interligne de la police — il variait d'un bloc à l'autre.
   Les 25 emplois de ce style perdent leur `<br />` du même coup. */
const etiquette: React.CSSProperties = { fontSize: 12.5, display: 'grid', gap: 4, minWidth: 0 };
const erreurStyle: React.CSSProperties = { color: 'var(--red-600)', fontSize: 12.5, marginTop: 6 };

/** Appel du moteur api::ged — `path` est relatif à `/api/ged/ged/`
 *  (ex. `manuel-ref/catalogue`, `manuel-circuit/etat`). */
export async function moteur(path: string, body?: unknown): Promise<{ ok: boolean; status: number; data: Record<string, unknown>; message: string }> {
  // ⚠️ CETTE FONCTION NE REJETTE JAMAIS. Un échec réseau revient comme un
  // refus ordinaire (`ok: false`, `status: 0`), pas comme une exception.
  //
  // Pourquoi c'est un invariant et pas une commodité : les quarante appels de
  // ce module s'écrivent `void moteur(…).then((r) => { setEnvoi(false); … })`,
  // SANS `.catch`. Si la promesse rejetait — moteur redémarré, réseau coupé,
  // onglet réveillé de veille — le `.then` ne s'exécutait pas, `envoi` restait
  // à `true`, et TOUS les boutons du panneau demeuraient désactivés sans un
  // mot. C'est le défaut rapporté par la RQ le 19/08/2026 : « les boutons
  // d'adoption ne fonctionnent pas ». Ils n'étaient pas cassés — ils étaient
  // grisés par un état que rien ne relâchait plus.
  //
  // Corriger ici plutôt qu'aux quarante appels : c'est le contrat de la
  // fonction, et un `.catch` oublié sur le quarante-et-unième appel
  // reproduirait le défaut à l'identique.
  let res: Response;
  try {
    res = await fetchEcriture(`/api/ged/ged/${path}`, {
      credentials: 'include',
      ...(body === undefined ? {} : {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }),
    });
  } catch {
    return {
      ok: false, status: 0, data: {},
      message: 'Le serveur n’a pas répondu. Rien n’a été enregistré — réessayez.',
    };
  }
  const data = await res.json().catch(() => ({}));
  const message = (data?.error?.message as string)
    || ((data?.impact as { message?: string })?.message)
    || (res.ok ? '' : `Refus (${res.status}).`);
  return { ok: res.ok, status: res.status, data: data || {}, message };
}

/** Lecture paginée d'une collection REST (maxLimit 100 — piège connu : une
 *  page unique tronquerait le parc à 100 équipements sur 245). */
export async function collection(nom: string, champs: string[], tri = 'code:asc'): Promise<Record<string, unknown>[]> {
  const tout: Record<string, unknown>[] = [];
  for (let page = 1; page <= 10; page += 1) {
    const q = champs.map((c, i) => `fields[${i}]=${c}`).join('&');
    const res = await fetch(
      `/api/ged/${nom}?${q}&sort=${tri}&pagination[page]=${page}&pagination[pageSize]=100`,
      { credentials: 'include' },
    );
    const d = await res.json().catch(() => null);
    const lignes = (d?.data as Record<string, unknown>[]) || [];
    tout.push(...lignes);
    if (lignes.length < 100) break;
  }
  return tout;
}

/* ═══════════════ V2 — Techniques ═══════════════════════════════════════════ */

export function OngletTechniques({ fiche, estRq, onEcrit }: {
  fiche: FicheDetail; estRq: boolean; onEcrit: () => void;
}) {
  const [equipements, setEquipements] = useState<Ref[]>([]);
  const [edition, setEdition] = useState<string | 'nouvelle' | null>(null);
  const [code, setCode] = useState(''); const [nom, setNom] = useState('');
  const [principe, setPrincipe] = useState(''); const [ordre, setOrdre] = useState('0');
  const [equipIds, setEquipIds] = useState<string[]>([]);
  const [erreur, setErreur] = useState(''); const [envoi, setEnvoi] = useState(false);
  /** Le registre COMPLET des techniques du laboratoire — pour en rattacher une
   *  qui existe déjà au lieu d'en recréer une qui porterait le même nom. */
  const [registre, setRegistre] = useState<FicheDetail['techniques']>([]);
  const [aRattacher, setARattacher] = useState('');

  useEffect(() => {
    let vivant = true;
    /* Équipements de MESURE seulement (RQ, 19/08/2026) : une technique se
       réalise sur un analyseur, pas sur une centrifugeuse ni un thermomètre
       étalon. Proposer les accessoires et la métrologie dans ce sélecteur
       invitait à des liaisons qui ne veulent rien dire. */
    void collection('ged-equipements', ['code', 'nom', 'nature']).then((rows) => {
      if (vivant) {
        setEquipements(rows
          .filter((r) => !r.nature || r.nature === 'mesure')
          .map((r) => ({ documentId: String(r.documentId), code: String(r.code), nom: String(r.nom || '') })));
      }
    });
    void moteur('manuel-ref/techniques').then((r) => {
      if (vivant && r.ok) setRegistre((r.data.techniques as FicheDetail['techniques']) || []);
    });
    return () => { vivant = false; };
  }, [fiche.documentId]);

  /** Rattacher une technique EXISTANTE à cet examen. Le moteur RATTACHE (il
   *  n'écrase plus l'unique fiche) : la technique reste liée à ses autres
   *  analyses. */
  const rattacher = () => {
    if (!aRattacher) return;
    setEnvoi(true); setErreur('');
    void moteur(`manuel-ref/techniques/${aRattacher}`, { ficheDocumentId: fiche.documentId }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      setARattacher(''); onEcrit();
    });
  };

  /** Détacher de CET examen seulement — la technique et ses autres liens
   *  restent en place. Ce n'est pas une suppression : une technique se
   *  désactive, elle ne s'efface pas. */
  const detacher = (t: FicheDetail['techniques'][number]) => {
    setErreur('');
    void moteur(`manuel-ref/techniques/${t.documentId}`, { detacherFicheDocumentId: fiche.documentId }).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  const dejaLiees = new Set(fiche.techniques.map((t) => t.documentId));
  const disponibles = registre.filter((t) => !dejaLiees.has(t.documentId));

  const ouvrir = (t?: FicheDetail['techniques'][number]) => {
    setErreur('');
    if (!t) {
      setEdition('nouvelle'); setCode(''); setNom(''); setPrincipe(''); setOrdre('0'); setEquipIds([]);
      return;
    }
    setEdition(t.documentId); setCode(t.code); setNom(t.nom);
    setPrincipe(t.principe || ''); setOrdre(String(t.ordre));
    setEquipIds(t.equipements.map((e) => e.documentId));
  };

  const enregistrer = () => {
    setEnvoi(true); setErreur('');
    const corps = {
      ...(edition === 'nouvelle' ? { code } : {}),
      nom, principe: principe || null, ordre: Number(ordre),
      ficheDocumentId: fiche.documentId, equipementIds: equipIds,
    };
    void moteur(edition === 'nouvelle' ? 'manuel-ref/techniques' : `manuel-ref/techniques/${edition}`, corps).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      setEdition(null); onEcrit();
    });
  };
  const basculer = (t: FicheDetail['techniques'][number]) => {
    void moteur(`manuel-ref/techniques/${t.documentId}`, { actif: !t.actif }).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  return (
    <div>
      {/* En tête et à droite. Il était SOUS le tableau, à côté du sélecteur de
          rattachement : sur un examen déjà pourvu, il fallait dérouler toute la
          liste pour trouver comment en ajouter une. */}
      {estRq && edition === null && (
        <div style={{ display: 'flex', marginBottom: 6 }}>
          <BoutonCreer onClick={() => ouvrir()}>Créer une technique</BoutonCreer>
        </div>
      )}
      <Tableau
        id="fiche-techniques"
        ajusterLargeur
        /* Défile DANS sa boîte : sans cela, la fenêtre de l'examen débordait en largeur (RQ, 14/09/2026). */
        defilementHorizontal
        /* L6b (10/10/2026) : niveau STANDARD ; Désactiver / Détacher en colonne d'actions (É-9). */
        niveau="standard"
        lignes={fiche.techniques}
        cleLigne={(t) => t.documentId}
        /* Le clic ouvre la technique ; « Désactiver » et « Détacher » restent —
           ils ne sont pas le geste par défaut. */
        ligneProps={(t) => (estRq
          ? ligneOuvrable(() => ouvrir(t), `Modifier ${t.code}`, { opacity: t.actif ? 1 : 0.5 })
          : { style: { opacity: t.actif ? 1 : 0.5 } })}
        colonnes={[
          { cle: 'code', libelle: 'Code', valeur: (t) => t.code, rendu: (t) => <strong>{t.code}</strong> },
          {
            cle: 'nom', libelle: 'Technique', valeur: (t) => t.nom,
            rendu: (t) => (
              <>
                {t.nom}
                {t.principe ? <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{t.principe}</div> : null}
              </>
            ),
          },
          {
            cle: 'equipements', libelle: 'Équipements', type: 'nombre',
            valeur: (t) => t.equipements.length,
            rendu: (t) => t.equipements.map((e) => e.code).join(', ') || '—',
          },
          {
            // Les AUTRES analyses qui l'empruntent : c'est ce qui dit qu'une
            // modification ici se verra ailleurs. Trié sur leur NOMBRE — les
            // techniques les plus partagées d'abord, ce sont les plus risquées
            // à modifier.
            cle: 'partage', libelle: 'Partagée avec',
            valeur: (t) => (t.fiches || []).filter((f) => f.documentId !== fiche.documentId).length,
            rendu: (t) => {
              const autres = (t.fiches || []).filter((f) => f.documentId !== fiche.documentId);
              return autres.length
                ? (
                  <span title={autres.map((f) => `${f.code} — ${f.libelle}`).join('\n')}>
                    {autres.length} autre{autres.length > 1 ? 's' : ''} analyse{autres.length > 1 ? 's' : ''}
                  </span>
                )
                : <span style={{ color: 'var(--text-muted)' }}>propre à cet examen</span>;
            },
          },
          { cle: 'ordre', libelle: 'Ordre', valeur: (t) => t.ordre, type: 'nombre', rendu: (t) => t.ordre },
        ]}
        actions={estRq ? (t) => (
          <>
            <Bouton taille="sm" onClick={() => basculer(t)}>
              {t.actif ? 'Désactiver' : 'Réactiver'}
            </Bouton>
            <Bouton taille="sm" onClick={() => detacher(t)} title="Retirer cette technique de CET examen — elle reste au registre et sur ses autres analyses">
              Détacher
            </Bouton>
          </>
        ) : undefined}
      />
      {!fiche.techniques.length && (
        <EmptyState titre="Aucune technique déclarée"
          indice="Un examen sans technique s’affiche sans automate ni méthode sur la vitrine — rien de bloquant, mais rien de dit." />
      )}
      {estRq && edition === null && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          {/* Rattacher plutôt que recréer : une technique déjà au registre
              (ECLIA, PCR en temps réel…) se partage entre analyses — la
              recréer sous un autre code ferait deux fois le même savoir-faire,
              avec deux listes d'équipements à tenir. */}
          {disponibles.length > 0 && (
            <label style={etiquette}><span>Rattacher une technique existante</span>
              <select value={aRattacher} onChange={(e) => setARattacher(e.currentTarget.value)} style={{ ...inputStyle, minWidth: 280, width: 'auto', fontSize: 12.5 }}>
                <option value="">—</option>
                {disponibles.map((t) => (
                  <option key={t.documentId} value={t.documentId}>
                    {t.code} — {t.nom}{(t.fiches || []).length ? ` (${(t.fiches || []).length} analyse${(t.fiches || []).length > 1 ? 's' : ''})` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {aRattacher && (
            <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={rattacher}>
              Rattacher
            </Bouton>
          )}
        </div>
      )}
      {/* ⚠️ `Modale`, `inputStyle` et pastilles — cet éditeur était une carte
          posée sous le tableau, avec des intitulés suivis d'un `<br />`, des
          champs à la géométrie du navigateur et les équipements en
          `<select multiple>`, c'est-à-dire au Ctrl-clic : le geste écarté par
          la RQ le 18/08 (« c'est bizarre comme mode »), où un clic sans Ctrl
          vide la sélection entière.
          ⚠️ Il n'a PAS été fusionné avec `FormTechnique` du registre, malgré
          l'apparence : celui-ci poste `ficheDocumentId` — il crée la technique
          ET la rattache à cet examen — quand l'autre porte en plus `actif` et
          les paramètres mesurés. Les deux n'écrivent pas la même chose ; les
          confondre est une décision, pas un nettoyage. */}
      {edition !== null && (
        <Modale
          titre={edition === 'nouvelle' ? 'Déclarer une technique' : `Modifier ${code}`}
          sousTitre={`Elle sera rattachée à ${fiche.code}. Une technique se partage entre les analyses qui l’empruntent.`}
          onClose={() => setEdition(null)}
          largeur={760}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setEdition(null)}>Annuler</Bouton>
              <Bouton variante="primaire" disabled={envoi} onClick={enregistrer}>
                {envoi ? 'Enregistrement…' : 'Enregistrer'}
              </Bouton>
            </div>
          )}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {edition === 'nouvelle' && (
                <label style={{ ...etiquette, flex: '0 1 150px' }}>
                  <span>Code</span>
                  <input value={code} onChange={(e) => setCode(e.currentTarget.value)}
                    placeholder="ECLIA" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
                </label>
              )}
              <label style={{ ...etiquette, flex: '1 1 240px' }}>
                <span>Nom</span>
                <input value={nom} onChange={(e) => setNom(e.currentTarget.value)}
                  placeholder="Électrochimiluminescence" style={inputStyle} />
              </label>
              <label style={{ ...etiquette, flex: '0 1 110px' }}>
                <span>Ordre</span>
                <input type="number" value={ordre} onChange={(e) => setOrdre(e.currentTarget.value)} style={inputStyle} />
              </label>
            </div>
            <label style={etiquette}>
              <span>Principe / méthode</span>
              <textarea value={principe} onChange={(e) => setPrincipe(e.currentTarget.value)} rows={3}
                placeholder="Ce qui est mesuré, et comment."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical', lineHeight: 1.5 }} />
            </label>
            <div style={etiquette}>
              <span>Équipements du parc</span>
              <SelecteurPastilles
                options={equipements.map((e) => ({ documentId: e.documentId, code: e.code, nom: e.nom || '' }))}
                choisis={equipIds} onChange={setEquipIds} modifiable
                placeholder="Ajouter un automate — code ou nom…"
                ariaLabel="Rechercher un équipement qui réalise cette technique"
                unite="automate"
                vide="Aucun coché — technique déclarée sans automate." />
            </div>
            {erreur && <div role="alert" style={erreurStyle}>{erreur}</div>}
          </div>
        </Modale>
      )}
    </div>
  );
}

/* ═══════════════ V3 — Intervalles de référence ═════════════════════════════ */

const SEXES = [['tous', 'Tous'], ['feminin', 'Féminin'], ['masculin', 'Masculin']] as const;
const GROSSESSES = [
  ['indifferent', 'Indifférent'], ['hors-grossesse', 'Hors grossesse'],
  ['grossesse', 'Grossesse'], ['t1', '1er trimestre'], ['t2', '2e trimestre'], ['t3', '3e trimestre'],
] as const;

/** Ce qu'on écrit le plus souvent dans « Condition » — de simples propositions,
 *  le champ reste libre. Tirées des 27 intervalles éclatés le 19/08/2026. */
const CONDITIONS_USUELLES = [
  'Phase folliculaire', 'Pic ovulatoire', 'Phase lutéale', 'Ménopause',
  'Couché', 'Debout 2 h', 'Matin 8 h', 'Soir 20 h', 'À jeun', 'Adulte', 'Enfant',
] as const;

function strateTexte(i: FicheDetail['intervalles'][number]): string {
  const sexe = i.sexe === 'tous' ? 'tous' : i.sexe === 'feminin' ? '♀' : '♂';
  /* L'âge dans l'unité où on le compte, et RIEN quand aucune borne n'est
     posée : « 0–130 ans » se lisait comme une restriction alors qu'il n'y en
     a aucune. La règle est celle de la fiche publique — socle partagé. */
  const ages = trancheAgeLisible(i.ageMin, i.ageMax) || 'tous âges';
  const gros = i.grossesse === 'indifferent' ? '' : ` · ${i.grossesse}`;
  /* La CONDITION, sans quoi les quatre bornes de l'œstradiol s'affichaient
     toutes « ♀ 0–130 ans » : quatre lignes rigoureusement identiques à l'œil,
     et impossible de savoir laquelle on clôture. */
  const cond = i.condition ? ` · ${i.condition}` : '';
  const tech = i.technique ? ` · ${i.technique.code}` : '';
  return `${sexe} ${ages}${gros}${cond}${tech}`;
}

export function OngletIntervalles({ fiche, estRq, onEcrit }: {
  fiche: FicheDetail; estRq: boolean; onEcrit: () => void;
}) {
  const { demanderTexte, confirmation } = useConfirmation();
  const [historique, setHistorique] = useState(false);
  const [creation, setCreation] = useState(false);
  const [sexe, setSexe] = useState('tous'); const [grossesse, setGrossesse] = useState('indifferent');
  const [condition, setCondition] = useState('');
  const [ageMin, setAgeMin] = useState(''); const [ageMax, setAgeMax] = useState('');
  const [borneMin, setBorneMin] = useState(''); const [borneMax, setBorneMax] = useState('');
  const [texteLibre, setTexteLibre] = useState(''); const [unite, setUnite] = useState('');
  const [source, setSource] = useState(''); const [dateApplication, setDateApplication] = useState('');
  const [techniqueId, setTechniqueId] = useState(''); const [cloreAvant, setCloreAvant] = useState(false);
  const [erreur, setErreur] = useState(''); const [envoi, setEnvoi] = useState(false);

  const visibles = fiche.intervalles
    .filter((i) => (historique ? true : !i.dateFin))
    .sort((a, b) => (a.dateFin ? 1 : 0) - (b.dateFin ? 1 : 0) || (a.ageMin ?? 0) - (b.ageMin ?? 0));

  const creer = () => {
    setEnvoi(true); setErreur('');
    void moteur('manuel-ref/intervalles', {
      ficheDocumentId: fiche.documentId,
      sexe, grossesse,
      condition: condition.trim() || null,
      ageMin: ageMin.trim() === '' ? null : Number(ageMin),
      ageMax: ageMax.trim() === '' ? null : Number(ageMax),
      borneMin: borneMin.trim() === '' ? null : Number(borneMin),
      borneMax: borneMax.trim() === '' ? null : Number(borneMax),
      texteLibre: texteLibre || null, unite: unite || null,
      source, dateApplication,
      techniqueDocumentId: techniqueId || null,
      cloreAvant,
    }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      setCreation(false); onEcrit();
    });
  };
  const cloturer = async (id: string) => {
    const dateFin = await demanderTexte(
      'Archiver cet intervalle — à partir de quelle date ne s’applique-t-il plus ?\n\n'
      + 'La date compte : les résultats rendus avant elle ont été interprétés avec cette borne.',
      { titre: 'Archiver l’intervalle', confirmer: 'Archiver', type: 'date', valeurInitiale: aujourdhuiIso() },
    );
    if (!dateFin) return;
    void moteur(`manuel-ref/intervalles/${id}/cloturer`, { dateFin }).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  /* LE CHEMIN DE RETOUR (RQ, 19/08/2026). Partout où l'on peut clore, on doit
     pouvoir rouvrir : la donnée n'est jamais effacée, mais un écran qui ne sait
     pas revenir en arrière équivaut à une perte pour qui l'utilise. */
  const rouvrir = async (id: string) => {
    const motif = await demanderTexte(
      'Remettre cet intervalle en vigueur ?\n\nSon archivage reste au journal, la restauration s’y ajoute.\n\nMotif :',
      { titre: 'Restaurer l’intervalle', confirmer: 'Restaurer', min: 5, valeurInitiale: 'Archivé par erreur' },
    );
    if (!motif) return;
    void moteur(`manuel-ref/intervalles/${id}/rouvrir`, { motif }).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      setErreur(''); onEcrit();
    });
  };

  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 6 }}>
        Un intervalle ne se modifie pas : il s’<strong>archive</strong> et un autre s’ouvre — l’historique
        reste consultable avec ses périodes de validité.{' '}
        <label><input type="checkbox" checked={historique} onChange={(e) => setHistorique(e.currentTarget.checked)} /> montrer l’historique</label>
      </div>
      {/* Le bouton de création EN TÊTE et à droite, comme partout ailleurs
          (RQ, 19/08 : « il doit se placer, et tous les autres d'ailleurs,
          toujours en haut de la table à droite »). Il était sous le tableau :
          sur un examen à quinze intervalles, il fallait dérouler pour en
          ajouter un seizième. */}
      {estRq && !creation && (
        <div style={{ display: 'flex', marginBottom: 6 }}>
          <BoutonCreer onClick={() => { setCreation(true); setErreur(''); }}>Adopter un intervalle</BoutonCreer>
        </div>
      )}
      <Tableau
        id="fiche-intervalles"
        ajusterLargeur
        /* Défile DANS sa boîte : sans cela, la fenêtre de l'examen débordait en largeur (RQ, 14/09/2026). */
        defilementHorizontal
        /* L6b (10/10/2026) : niveau AVANCÉ — la STRATE (âge, sexe, grossesse) épinglée,
           masquage, filtres ; Archiver / Restaurer en colonne d'actions (É-9). */
        niveau="avance"
        lignes={visibles}
        cleLigne={(i) => i.documentId}
        ligneProps={(i) => ({ style: { opacity: i.dateFin ? 0.55 : 1 } })}
        colonnes={[
          {
            cle: 'strate', libelle: 'Strate', valeur: (i) => strateTexte(i), epinglee: true, obligatoire: true,
            rendu: (i) => (
              <>
                {strateTexte(i)}
                {/* L'ORIGINE, dite à côté de la valeur et non en légende : une
                    borne héritée d'un paramètre ne se modifie pas ici, et
                    laisser croire le contraire ferait chercher un bouton qui
                    n'existe pas (25/08/2026). */}
                {i.origine === 'parametre' && i.analyte && (
                  <span title={`Valeur usuelle du paramètre ${i.analyte.code} — elle vaut pour cet examen parce qu’il le contient. Elle se modifie sur la fiche du paramètre.`}
                    style={{ marginInlineStart: 8, fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--cyan-50)', color: 'var(--cyan-700)', border: '1px solid var(--cyan-200)' }}>
                    {i.analyte.code}
                  </span>
                )}
              </>
            ),
          },
          {
            cle: 'intervalle', libelle: 'Intervalle',
            // La borne basse NUMÉRIQUE : trier sur « 9 – 12 » et « 10 – 14 »
            // comme du texte classerait 10 avant 9.
            valeur: (i) => (i.borneMin ?? i.borneMax ?? i.texteLibre ?? ''),
            rendu: (i) => i.texteLibre || `${i.borneMin ?? '<'} – ${i.borneMax ?? '>'}`,
          },
          { cle: 'unite', libelle: 'Unité', valeur: (i) => i.unite || '', type: 'liste', rendu: (i) => i.unite || '—' },
          { cle: 'source', libelle: 'Source', valeur: (i) => i.source || '', type: 'liste', rendu: (i) => i.source || '—' },
          {
            cle: 'validite', libelle: 'Validité',
            // Les lignes EN VIGUEUR d'abord — ce sont celles qu'on rend.
            valeur: (i) => (i.dateFin ? `1-${i.dateFin}` : `0-${i.dateApplication}`),
            rendu: (i) => <>{i.dateApplication}{i.dateFin ? ` → ${i.dateFin}` : ' → en vigueur'}</>,
          },
        ]}
        actions={estRq ? (i) => {
              /* Rien à faire sur une valeur HÉRITÉE : archiver ici la retirerait
                 du paramètre, donc de tous les autres examens qui l'emploient —
                 un geste local aux conséquences ailleurs, invisible depuis ici. */
              if (i.origine === 'parametre') {
                return (
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}
                    title="Cette valeur appartient au paramètre : elle se modifie sur sa fiche, où l’on voit tous les examens concernés.">
                    sur la fiche du paramètre
                  </span>
                );
              }
              return i.dateFin
                ? (
                  <Bouton taille="sm" title={`Archivé le ${fmtDate(i.dateFin)}. Le remettre en vigueur.`} onClick={() => rouvrir(i.documentId)}>
                    Restaurer
                  </Bouton>
                )
                : (
                  <Bouton taille="sm" title="Retire cette valeur de la fiche publique et la range aux archives. Réversible depuis cet écran." onClick={() => cloturer(i.documentId)}>
                    Archiver
                  </Bouton>
                );
        } : undefined}
      />
      {!visibles.length && (
        <EmptyState titre="Aucun intervalle adopté"
          indice="Ni sur cet examen, ni sur ses paramètres. La vitrine continue d’afficher le texte libre historique (valeursRef) tant que le biologiste n’a pas adopté de valeurs." />
      )}
      {/* `Modale` : c'était une carte posée sous le tableau, et son pied
          rendait « Adopter » AVANT « Annuler » — l'inverse de l'ordre du
          composant `Modale` et des autres pieds de l'application. */}
      {creation && (
        <Modale
          titre={`Adopter un intervalle — ${fiche.code}`}
          sousTitre="Un intervalle ne se modifie pas : il se clôt, et un autre s’ouvre. Le moteur refuse le chevauchement de strates."
          onClose={() => setCreation(false)}
          largeur={860}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setCreation(false)}>Annuler</Bouton>
              <Bouton variante="primaire" disabled={envoi} onClick={creer}>
                {envoi ? 'Adoption…' : 'Adopter'}
              </Bouton>
            </div>
          )}>
          <div style={rangee}>
            <label style={etiquette}><span>Sexe</span>
              <select value={sexe} onChange={(e) => setSexe(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
                {SEXES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label style={etiquette}><span>Grossesse</span>
              <select value={grossesse} onChange={(e) => setGrossesse(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
                {GROSSESSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {/* Champ LIBRE : la liste des conditions utiles n'est pas close.
                Les propositions ne l'enferment pas — elles évitent seulement
                que « folliculaire », « Folliculaire » et « phase folliculaire »
                deviennent trois conditions distinctes dans la même fiche. */}
            <label style={etiquette}><span>Condition</span>
              <input value={condition} onChange={(e) => setCondition(e.currentTarget.value)}
                list="conditions-intervalle" placeholder="phase, posture, heure…" style={{ ...inputStyle, width: 165 }} />
              <datalist id="conditions-intervalle">
                {CONDITIONS_USUELLES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </label>
            <label style={etiquette}><span>Âge min (années)</span><input value={ageMin} onChange={(e) => setAgeMin(e.currentTarget.value)} style={{ ...inputStyle, width: 90 }} /></label>
            <label style={etiquette}><span>Âge max</span><input value={ageMax} onChange={(e) => setAgeMax(e.currentTarget.value)} style={{ ...inputStyle, width: 90 }} /></label>
            <label style={etiquette}><span>Technique</span>
              <select value={techniqueId} onChange={(e) => setTechniqueId(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
                <option value="">Toutes</option>
                {fiche.techniques.map((t) => <option key={t.documentId} value={t.documentId}>{t.code}</option>)}
              </select>
            </label>
          </div>
          <div style={rangee}>
            <label style={etiquette}><span>Borne min</span><input value={borneMin} onChange={(e) => setBorneMin(e.currentTarget.value)} style={{ ...inputStyle, width: 90 }} /></label>
            <label style={etiquette}><span>Borne max</span><input value={borneMax} onChange={(e) => setBorneMax(e.currentTarget.value)} style={{ ...inputStyle, width: 90 }} /></label>
            <label style={etiquette}><span>ou rendu qualitatif</span><input value={texteLibre} placeholder="Négatif, Absence…" onChange={(e) => setTexteLibre(e.currentTarget.value)} style={{ ...inputStyle, width: 150 }} /></label>
            <label style={etiquette}><span>Unité</span><input value={unite} onChange={(e) => setUnite(e.currentTarget.value)} style={{ ...inputStyle, width: 90 }} /></label>
          </div>
          <div style={rangee}>
            <label style={etiquette}><span>Source (obligatoire)</span><input value={source} placeholder="Notice fabricant, SFBC, étude locale…" onChange={(e) => setSource(e.currentTarget.value)} style={{ ...inputStyle, width: 280 }} /></label>
            <label style={etiquette}><span>Date d’application</span><input type="date" value={dateApplication} onChange={(e) => setDateApplication(e.currentTarget.value)} style={{ ...inputStyle, width: 'auto' }} /></label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, alignSelf: 'center' }}>
              <input type="checkbox" checked={cloreAvant} onChange={(e) => setCloreAvant(e.currentTarget.checked)} />
              clore l’existant à la veille
            </label>
          </div>
          {erreur && <div role="alert" style={erreurStyle}>{erreur}</div>}
        </Modale>
      )}
      {confirmation}
    </div>
  );
}

/* ═══════════════ V3 — Critères d'échantillon (rattachement par fiche) ══════ */

export function OngletCriteresFiche({ fiche, estRq, onEcrit }: {
  fiche: FicheDetail; estRq: boolean; onEcrit: () => void;
}) {
  const [tous, setTous] = useState<{ documentId: string; code: string; libelle: string; type: string; commun: boolean; actif: boolean }[]>([]);
  const [choix, setChoix] = useState('');
  const [proprCode, setProprCode] = useState('');
  const [proprLibelle, setProprLibelle] = useState('');
  const [proprType, setProprType] = useState<'refus' | 'acceptation'>('refus');
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    void collection('critere-echantillons', ['code', 'libelle', 'type', 'commun', 'actif']).then((rows) => {
      if (vivant) {
        setTous(rows.map((r) => ({
          documentId: String(r.documentId), code: String(r.code), libelle: String(r.libelle),
          type: String(r.type), commun: !!r.commun, actif: r.actif !== false,
        })));
      }
    });
    return () => { vivant = false; };
  }, []);

  const lies = new Set(fiche.criteresEchantillon.map((c) => c.documentId));
  const attachables = tous.filter((c) => c.actif && c.commun && !lies.has(c.documentId));

  const geste = (ajouter: string[], retirer: string[]) => {
    setErreur('');
    void moteur('manuel-ref/fiches/batch', {
      documentIds: [fiche.documentId], ajouterCriteres: ajouter, retirerCriteres: retirer,
    }).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      setChoix(''); onEcrit();
    });
  };

  return (
    <div>
      <Tableau
        id="fiche-criteres"
        ajusterLargeur
        /* L6b (10/10/2026) : niveau SIMPLE ; Détacher en colonne d'actions (É-9). */
        niveau="simple"
        lignes={fiche.criteresEchantillon}
        cleLigne={(c) => c.documentId}
        ligneProps={(c) => ({ style: { opacity: c.actif ? 1 : 0.5 } })}
        colonnes={[
          { cle: 'code', libelle: 'Code', valeur: (c) => c.code, rendu: (c) => <strong>{c.code}</strong> },
          { cle: 'libelle', libelle: 'Critère', valeur: (c) => c.libelle, rendu: (c) => c.libelle },
          {
            cle: 'type', libelle: 'Type', valeur: (c) => c.type,
            rendu: (c) => (c.type === 'refus' ? 'Refus' : 'Acceptation'),
          },
          {
            // Les critères COMMUNS d'abord : ils s'appliquent partout, et c'est
            // ce qu'on relit en premier quand on vérifie une fiche.
            cle: 'portee', libelle: 'Portée', valeur: (c) => (c.commun ? 0 : 1),
            rendu: (c) => (c.commun ? 'Commun' : 'Propre à cette fiche'),
          },
        ]}
        actions={estRq ? (c) => <Bouton taille="sm" onClick={() => geste([], [c.documentId])}>Détacher</Bouton> : undefined}
      />
      {!fiche.criteresEchantillon.length && (
        <EmptyState titre="Aucun critère rattaché"
          indice="Les critères communs (hémolyse, tube non conforme, délai dépassé…) se définissent une fois dans l’onglet Critères de l’écran, puis se rattachent ici — ou en batch depuis le catalogue." />
      )}
      {estRq && (
        <div style={{ marginTop: 8, fontSize: 12.5 }}>
          <select value={choix} onChange={(e) => setChoix(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
            <option value="">Rattacher un critère commun…</option>
            {attachables.map((c) => <option key={c.documentId} value={c.documentId}>{c.code} — {c.libelle}</option>)}
          </select>
          <Bouton variante="primaire" taille="sm" disabled={!choix} onClick={() => geste([choix], [])} style={{ marginLeft: 6 }}>
            Rattacher
          </Bouton>
        </div>
      )}

      {/* Un critère PROPRE à cet examen (RQ, 18/08) : ce que le registre commun
          ne couvre pas — « à jeun strict contrôlé », « prélever avant la dose ».
          Créé `commun: false` et lié à CETTE fiche : le moteur refuse de le
          lier à une deuxième — un critère qui sert deux examens est commun. */}
      {estRq && (
        <div style={{ marginTop: 12, padding: 10, border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
          <TitreRubrique>Créer un critère propre à cet examen</TitreRubrique>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={proprCode} placeholder="Code (ex. CRIT-JEUN)" style={{ ...inputStyle, width: 150, fontSize: 12.5 }}
              onChange={(e) => setProprCode(e.currentTarget.value.toUpperCase())} />
            <input value={proprLibelle} placeholder="Libellé du critère…" style={{ ...inputStyle, flex: '1 1 240px', width: 'auto', fontSize: 12.5 }}
              onChange={(e) => setProprLibelle(e.currentTarget.value)} />
            <select value={proprType} onChange={(e) => setProprType(e.currentTarget.value as 'refus' | 'acceptation')} style={{ ...inputStyle, width: 'auto', fontSize: 12.5 }}>
              <option value="refus">Refus</option>
              <option value="acceptation">Acceptation sous réserve</option>
            </select>
            <Bouton variante="primaire" taille="sm" disabled={!proprCode.trim() || !proprLibelle.trim()} onClick={() => {
                setErreur('');
                void moteur('manuel-ref/criteres', {
                  code: proprCode.trim(), libelle: proprLibelle.trim(), type: proprType,
                  commun: false, ficheIds: [fiche.documentId],
                }).then((r) => {
                  if (!r.ok) { setErreur(r.message); return; }
                  setProprCode(''); setProprLibelle(''); onEcrit();
                });
              }}>
              Créer et rattacher
            </Bouton>
          </div>
        </div>
      )}
      {erreur && <div style={erreurStyle}>{erreur}</div>}
    </div>
  );
}

/* ═══════════════ V3 — Le référentiel des critères (onglet d'écran) ═════════ */

export function OngletCriteresReferentiel({ estRq }: { estRq: boolean }) {
  const [lignes, setLignes] = useState<{ documentId: string; code: string; libelle: string; type: string; description: string | null; commun: boolean; actif: boolean }[] | null>(null);
  const [version, setVersion] = useState(0);
  const [edition, setEdition] = useState<string | 'nouveau' | null>(null);
  const [code, setCode] = useState(''); const [libelle, setLibelle] = useState('');
  const [type, setType] = useState('refus'); const [description, setDescription] = useState('');
  const [commun, setCommun] = useState(true);
  const [erreur, setErreur] = useState(''); const [envoi, setEnvoi] = useState(false);
  const [impact, setImpact] = useState<string>('');

  useEffect(() => {
    let vivant = true;
    void collection('critere-echantillons', ['code', 'libelle', 'type', 'description', 'commun', 'actif']).then((rows) => {
      if (vivant) {
        setLignes(rows.map((r) => ({
          documentId: String(r.documentId), code: String(r.code), libelle: String(r.libelle),
          type: String(r.type), description: (r.description as string) || null,
          commun: !!r.commun, actif: r.actif !== false,
        })));
      }
    });
    return () => { vivant = false; };
  }, [version]);

  if (!lignes) return <Skeleton rows={5} />;

  const ouvrir = (c?: NonNullable<typeof lignes>[number]) => {
    setErreur(''); setImpact('');
    if (!c) { setEdition('nouveau'); setCode(''); setLibelle(''); setType('refus'); setDescription(''); setCommun(true); return; }
    setEdition(c.documentId); setCode(c.code); setLibelle(c.libelle);
    setType(c.type); setDescription(c.description || ''); setCommun(c.commun);
  };
  const enregistrer = (confirmerImpact = false) => {
    setEnvoi(true); setErreur('');
    const corps = {
      ...(edition === 'nouveau' ? { code } : {}),
      libelle, type, description: description || null, commun, confirmerImpact,
    };
    void moteur(edition === 'nouveau' ? 'manuel-ref/criteres' : `manuel-ref/criteres/${edition}`, corps).then((r) => {
      setEnvoi(false);
      if (r.status === 409) { setImpact(r.message); return; }
      if (!r.ok) { setErreur(r.message); return; }
      setEdition(null); setImpact(''); setLignes(null); setVersion((v) => v + 1);
    });
  };

  return (
    <div>
      {/* En tête et à droite, comme les autres listes (RQ, 19/08). */}
      {estRq && edition === null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 6 }}>
          {/* Ce qui est tracé se VOIT (24/08/2026) : les révisions des
              critères s'écrivaient sous « MAN-CRITERES » sans accès. */}
          <BoutonJournal reference="MAN-CRITERES" quoi="des critères d'échantillon" />
          <BoutonCreer onClick={() => ouvrir()}>Nouveau critère</BoutonCreer>
        </div>
      )}
      <Tableau
        id="registre-criteres"
        ajusterLargeur
        /* L6b (10/10/2026) : niveau SIMPLE. */
        niveau="simple"
        lignes={lignes}
        cleLigne={(c) => c.documentId}
        /* Le clic sur la ligne OUVRE le critère : la colonne d'actions ne portait
           qu'un « Modifier » par ligne, répété autant de fois qu'il y a de
           critères, pour le geste qu'on attend déjà d'un clic. */
        ligneProps={(c) => (estRq
          ? ligneOuvrable(() => ouvrir(c), `Modifier ${c.code}`, { opacity: c.actif ? 1 : 0.5 })
          : { style: { opacity: c.actif ? 1 : 0.5 } })}
        colonnes={[
          {
            cle: 'code', libelle: 'Code', valeur: (c) => c.code,
            rendu: (c) => <strong>{c.code}</strong>,
          },
          {
            cle: 'libelle', libelle: 'Critère', valeur: (c) => c.libelle,
            rendu: (c) => (
              <>
                {c.libelle}
                {c.description ? <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{c.description}</div> : null}
              </>
            ),
          },
          {
            cle: 'type', libelle: 'Type', valeur: (c) => c.type,
            rendu: (c) => (c.type === 'refus' ? 'Refus' : 'Acceptation'),
          },
          {
            // Trié sur le booléen : les critères COMMUNS d'abord, parce que ce
            // sont eux qui s'appliquent partout et qu'on relit en premier.
            cle: 'portee', libelle: 'Portée', valeur: (c) => (c.commun ? 0 : 1),
            rendu: (c) => (c.commun ? 'Commun' : 'Propre'),
          },
        ]}
      />
      {!lignes.length && (
        <EmptyState titre="Aucun critère défini"
          indice="Les critères d’acceptation et de refus d’échantillon (préanalytiques) se définissent ici, puis se rattachent aux fiches — en unitaire ou en batch. Distincts des critères de PERFORMANCE analytique (rubrique dédiée)." />
      )}

      {edition !== null && (
        <Modale
          titre={edition === 'nouveau' ? 'Déclarer un critère' : `Modifier ${code}`}
          sousTitre="Critère préanalytique d’acceptation ou de refus. Un critère COMMUN s’applique à toutes les fiches qui le portent — le modifier les touche toutes."
          onClose={() => setEdition(null)}
          largeur={760}
          pied={(
            <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => setEdition(null)}>Annuler</Bouton>
              <Bouton variante="primaire" disabled={envoi} onClick={() => enregistrer()}>
                {envoi ? 'Enregistrement…' : 'Enregistrer'}
              </Bouton>
            </div>
          )}>
          <div style={{ ...rangee, marginTop: 0 }}>
            {edition === 'nouveau' && (
              <label style={etiquette}><span>Code</span><input value={code} onChange={(e) => setCode(e.currentTarget.value)} style={{ ...inputStyle, width: 110 }} /></label>
            )}
            <label style={etiquette}><span>Libellé</span><input value={libelle} onChange={(e) => setLibelle(e.currentTarget.value)} style={{ ...inputStyle, width: 280 }} /></label>
            <label style={etiquette}><span>Type</span>
              <select value={type} onChange={(e) => setType(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
                <option value="refus">Refus</option><option value="acceptation">Acceptation</option>
              </select>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, alignSelf: 'center' }}>
              <input type="checkbox" checked={commun} onChange={(e) => setCommun(e.currentTarget.checked)} />
              commun (mutualisable)
            </label>
          </div>
          <label style={{ ...etiquette, marginTop: 8 }}><span>Description</span>
            <input value={description} onChange={(e) => setDescription(e.currentTarget.value)} style={inputStyle} />
          </label>
          {impact && (
            <div style={{ ...card, padding: 10, marginTop: 8, background: 'var(--amber-50)', fontSize: 12.5 }}>
              {impact}
              <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => enregistrer(true)} style={{ marginLeft: 8 }}>
                Confirmer malgré l’impact
              </Bouton>
            </div>
          )}
          {erreur && <div role="alert" style={erreurStyle}>{erreur}</div>}
        </Modale>
      )}
    </div>
  );
}
export function OngletTechniquesRegistre({ estRq, filtreInitial = '', ouvrirExamen }: {
  estRq: boolean; filtreInitial?: string; ouvrirExamen?: (code: string) => void;
}) {
  type Technique = {
    documentId: string; code: string; nom: string; principe: string | null;
    ordre: number | null; actif: boolean;
    fiches: { documentId: string; code: string; libelle: string; archivee: boolean }[];
    equipements: { documentId: string; code: string; nom: string }[];
    analytes: { documentId: string; code: string; nom: string }[];
  };

  const [liste, setListe] = useState<Technique[] | null>(null);
  const [equipements, setEquipements] = useState<Ref[]>([]);
  const [parametres, setParametres] = useState<Ref[]>([]);
  const [version, setVersion] = useState(0);
  /* `filtreInitial` : la navigation croisée (« ouvrir cette technique » depuis
     un paramètre) arrive préfiltrée sur le code — l'utilisateur suit un lien,
     il ne doit pas retaper ce qu'il vient de cliquer. */
  const [q, setQ] = useState(filtreInitial);
  /* Ajusté au rendu, pas dans un effet : l'utilisateur suit un lien préfiltré,
     et une passe intermédiaire lui montrerait la liste ENTIÈRE avant de la
     filtrer — un clignotement sur trois cents lignes. */
  const [filtrePrec, setFiltrePrec] = useState(filtreInitial);
  if (filtreInitial !== filtrePrec) {
    setFiltrePrec(filtreInitial);
    if (filtreInitial) setQ(filtreInitial);
  }
  const [voirInactives, setVoirInactives] = useState(false);
  const [edition, setEdition] = useState<Technique | 'nouvelle' | null>(null);
  const [ouverte, setOuverte] = useState<Technique | null>(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    void moteur('manuel-ref/techniques').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); setListe([]); return; }
      setListe((r.data.techniques as Technique[]) || []);
    });
    return () => { vivant = false; };
  }, [version]);

  useEffect(() => {
    let vivant = true;
    /* Équipements de MESURE seulement (RQ, 19/08/2026) : une technique se
       réalise sur un analyseur, pas sur une centrifugeuse ni un thermomètre
       étalon. Proposer les accessoires et la métrologie dans ce sélecteur
       invitait à des liaisons qui ne veulent rien dire. */
    void collection('ged-equipements', ['code', 'nom', 'nature']).then((rows) => {
      if (vivant) {
        setEquipements(rows
          .filter((r) => !r.nature || r.nature === 'mesure')
          .map((r) => ({ documentId: String(r.documentId), code: String(r.code), nom: String(r.nom || '') })));
      }
    });
    void collection('ged-analytes', ['code', 'nom']).then((rows) => {
      if (vivant) setParametres(rows.map((r) => ({ documentId: String(r.documentId), code: String(r.code), nom: String(r.nom || '') })));
    });
    return () => { vivant = false; };
  }, []);

  if (!liste) return <Skeleton rows={5} />;

  const qn = q.trim().toLowerCase();
  const visibles = liste
    .filter((t) => (voirInactives ? true : t.actif))
    .filter((t) => !qn || `${t.code} ${t.nom} ${t.principe || ''}`.toLowerCase().includes(qn));
  const inactives = liste.filter((t) => !t.actif).length;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '86ch' }}>
        Une technique est un <strong>savoir-faire du laboratoire</strong> — immunoturbidimétrie,
        chimiluminescence, PCR en temps réel — et non une propriété d’une analyse : plusieurs
        analyses empruntent la même. Elle porte son principe et les équipements qui la réalisent.
      </p>

      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher une technique, un principe…" style={{ flex: '1 1 240px', maxWidth: 360, height: 34, padding: '0 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)' }} />
        {inactives > 0 && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={voirInactives} onChange={(e) => setVoirInactives(e.currentTarget.checked)} />
            voir les {inactives} désactivée{inactives > 1 ? 's' : ''}
          </label>
        )}
        {estRq && (
          <BoutonCreer onClick={() => setEdition('nouvelle')}>Déclarer une technique</BoutonCreer>
        )}
      </div>

      {visibles.length === 0 ? (
        <EmptyState
          titre={liste.length === 0 ? 'Aucune technique déclarée' : 'Aucune technique ne correspond'}
          indice={liste.length === 0
            ? 'Le registre est vide depuis la création du modèle : personne n’avait d’endroit où le remplir. Déclarez-y les techniques réellement employées — c’est ce que le dossier de méthode et l’inventaire du parc iront lire.'
            : 'Élargissez la recherche, ou affichez les techniques désactivées.'} />
      ) : (
        <div style={{ ...card, padding: 0 }}>
          <Tableau
            defilementHorizontal
            id="registre-techniques"
            ajusterLargeur
            /* L6b (10/10/2026) : niveau STANDARD ; le crayon de correction en colonne d'actions (É-9). */
            niveau="standard"
            lignes={visibles}
            cleLigne={(t) => t.documentId}
            ligneProps={(t) => ({
              onClick: () => setOuverte(t),
              style: { opacity: t.actif ? 1 : 0.6, cursor: 'pointer' },
            })}
            colonnes={[
              {
                cle: 'code', libelle: 'Code', valeur: (t) => t.code,
                style: { fontFamily: 'var(--font-mono)', fontWeight: 700, whiteSpace: 'nowrap' },
                rendu: (t) => (
                  <>
                    {t.code}
                    {!t.actif && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-700)' }}>désactivée</div>}
                  </>
                ),
              },
              {
                cle: 'nom', libelle: 'Nom', valeur: (t) => t.nom,
                style: { fontWeight: 600 }, rendu: (t) => t.nom,
              },
              {
                cle: 'principe', libelle: 'Principe',
                // Trié sur le principe COMPLET, pas sur les 160 caractères
                // affichés : deux techniques dont le principe commence pareil
                // se classeraient au hasard de la troncature.
                valeur: (t) => t.principe || '',
                style: { color: 'var(--text-muted)', maxWidth: 340 },
                rendu: (t) => (t.principe ? `${t.principe.slice(0, 160)}${t.principe.length > 160 ? '…' : ''}` : '—'),
              },
              {
                cle: 'equipements', libelle: 'Équipements', type: 'nombre',
                // Le NOMBRE et non la liste : trier cette colonne sert à trouver
                // les techniques qui ne sont reliées à aucun appareil — elles
                // remontent en tête.
                valeur: (t) => t.equipements.length,
                rendu: (t) => (t.equipements.length
                  ? t.equipements.map((e) => e.code).join(' · ')
                  : <span style={{ color: 'var(--amber-700)' }}>aucun</span>),
              },
              {
                // Les paramètres qu'elle MESURE — c'est ce qui permet de dire
                // qu'une NFS emploie deux techniques selon le paramètre, au lieu
                // d'une seule pour tout l'examen.
                cle: 'analytes', libelle: 'Paramètres', type: 'nombre',
                valeur: (t) => t.analytes.length,
                rendu: (t) => (t.analytes.length ? t.analytes.map((a) => a.code).join(' · ') : '—'),
              },
              {
                cle: 'analyses', libelle: 'Analyses', type: 'nombre',
                // On trie sur les analyses VIVANTES seulement : une technique
                // « partagée par 3 analyses » dont deux sont retirées du
                // catalogue n'est partagée par personne.
                valeur: (t) => t.fiches.filter((f) => !f.archivee).length,
                rendu: (t) => {
                  const vivantes = t.fiches.filter((f) => !f.archivee);
                  const archivees = t.fiches.length - vivantes.length;
                  return (
                    <>
                      {vivantes.length}
                      {archivees > 0 && (
                        <span style={{ color: 'var(--text-muted)' }}> (+{archivees} archivée{archivees > 1 ? 's' : ''})</span>
                      )}
                    </>
                  );
                },
              },
            ]}
            /* ⚠️ LES DEUX GESTES RESTENT DISTINCTS (Inès, 28/08) : la LIGNE ouvre la
               technique en consultation, ce BOUTON ouvre sa correction. L'icône seule
               (règle du 28/08), l'action nommée avec le CODE dans le nom accessible.
               `SansOuvrir` est posé par le socle. */
            actions={estRq ? (t) => <BoutonIcone icone="pen" action={`Modifier ${t.code}`} onClick={() => setEdition(t)} /> : undefined}
          />
        </div>
      )}

      {ouverte && <FenetreTechnique t={ouverte} onFerme={() => setOuverte(null)} ouvrirExamen={ouvrirExamen} />}

      {edition && (
        <FormTechnique
          technique={edition === 'nouvelle' ? null : edition}
          equipements={equipements}
          parametres={parametres}
          onFerme={() => setEdition(null)}
          onEcrit={() => { setEdition(null); setVersion((v) => v + 1); }}
        />
      )}
    </div>
  );
}

/**
 * La FENÊTRE d'une technique — « quand je clique sur une technique je vois tous
 * ses détails et à quels équipements et paramètres elle est reliée » (RQ,
 * 18/08/2026).
 *
 * En lecture : le registre reste le lieu de l'écriture (bouton « Modifier »).
 * Une fenêtre qui montre ET modifie mêlerait deux gestes que la RQ distingue —
 * consulter en routine, corriger de temps en temps.
 */
function FenetreTechnique({ t, onFerme, ouvrirExamen }: {
  t: {
    documentId: string; code: string; nom: string; principe: string | null; actif: boolean;
    equipements: Ref[]; analytes: Ref[];
    fiches: { documentId: string; code: string; libelle: string; archivee: boolean }[];
  };
  onFerme: () => void; ouvrirExamen?: (code: string) => void;
}) {
  const puce: React.CSSProperties = {
    display: 'inline-block', fontSize: 12.5, fontFamily: 'var(--font-mono)',
    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '1px 8px',
    marginRight: 4, marginBottom: 3, background: 'var(--surface-card)',
  };
  /* L'intitulé de la maison, avec la seule marge propre à cette fenêtre :
     les rubriques s'y suivent, il leur faut de l'air au-dessus. */
  const titre: React.CSSProperties = { ...styleRubrique, margin: '12px 0 4px' };
  const vivantes = t.fiches.filter((f) => !f.archivee);
  /* ⚠️ `Modale`, comme les quarante-trois autres dialogues de l'application.
     Cette fenêtre-ci était dessinée à la main, et son formulaire jumeau aussi :
     la MÊME technique s'ouvrait de deux façons selon qu'on la consultait ou
     qu'on la corrigeait. Trois écarts partaient avec le voile artisanal —
     un titre au corps du texte au lieu de 16/700, aucune croix de fermeture,
     et la touche Échap sans effet.

     Le quatrième n'était pas cosmétique : `zIndex: 120`. C'est exactement la
     couche que la note des COUCHES (ui.tsx) donne en exemple du défaut — sous
     le témoin « l'application travaille » (1000) et sous le bandeau d'alerte
     (1010). `Modale` est à 400, donc sous les deux : un message d'erreur reste
     lisible par-dessus cette fenêtre. */
  return (
    <Modale
      titre={`${t.code} — ${t.nom}`}
      sousTitre={t.actif
        ? 'Technique du registre — le rattachement se corrige par « Modifier », au registre.'
        : (
          <span style={{ color: 'var(--amber-700)', fontWeight: 600 }}>
            Technique désactivée — elle reste citée par les analyses et les dossiers de méthode déjà signés.
          </span>
        )}
      onClose={onFerme}
      largeur={820}
      pied={(
        <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={onFerme}>Fermer</Bouton>
        </div>
      )}
    >
      {/* `marginTop` de la première rubrique annulé : `Modale` pose déjà son
          rembourrage de 18 px, la marge du style `titre` s'y ajoutait et
          creusait un blanc au-dessus de « Principe de la méthode ». */}
      <div style={{ ...titre, marginTop: 0 }}>Principe de la méthode</div>
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>
        {t.principe || <span style={{ color: 'var(--amber-700)' }}>Non renseigné — le dossier de méthode et le livret iront le lire ici.</span>}
      </div>

      <div style={titre}>Équipements qui la réalisent ({t.equipements.length})</div>
      {t.equipements.length
        ? t.equipements.map((e) => <span key={e.documentId} style={puce} title={e.nom}>{e.code} — {e.nom}</span>)
        : (
          <div style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>
            Aucun. Le rattachement n’était pas déductible du parc — à poser par « Modifier ».
          </div>
        )}

      <div style={titre}>Paramètres qu’elle mesure ({t.analytes.length})</div>
      {t.analytes.length
        ? t.analytes.map((a) => <span key={a.documentId} style={puce} title={a.nom}>{a.code}</span>)
        : <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun paramètre rattaché — la technique est portée au niveau de l’examen.</div>}

      <div style={titre}>Analyses qui l’empruntent ({vivantes.length})</div>
      {vivantes.length
        ? vivantes.map((f) => (
          <button key={f.documentId} style={{ ...puce, cursor: ouvrirExamen ? 'pointer' : 'default' }}
            title={f.libelle} onClick={() => ouvrirExamen?.(f.code)}>{f.code}</button>
        ))
        : <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune analyse active.</div>}
    </Modale>
  );
}

/**
 * Un tableau de cases à cocher — le parc, puis le référentiel des paramètres.
 *
 * ⚠️ LE LIBELLÉ REVIENT À LA LIGNE, IL NE SE COUPE PLUS.
 *
 * Les deux listes étaient en `nowrap` + points de suspension dans des colonnes
 * de 210 px : « EQ17 — Mini-iSED (vitesse de sédi… », « EQ25 — DocuReader 2 —
 * analyseu… ». Or c'est ICI qu'on choisit l'automate, et le nom coupé tombait
 * précisément là où deux appareils se distinguent. Aucune infobulle ne
 * rattrapait la coupe : le texte était perdu, pas caché.
 *
 * Les colonnes passent à 240 px et le libellé s'enroule sur deux lignes.
 * `alignItems: flex-start` et la marge de la case la gardent en regard de la
 * PREMIÈRE ligne — centrée, elle glissait au milieu du bloc sur les libellés
 * longs et l'on ne savait plus quelle ligne elle cochait.
 *
 * ⚠️ Composant de MODULE, et non défini dans le corps de `FormTechnique` :
 * déclarée à l'intérieur, la fonction change d'identité à chaque rendu, React
 * démonte et remonte tout le tableau à chaque case cochée — et le clavier perd
 * le focus au premier appui sur Espace.
 */
function CasesACocher({ items, coches, bascule, vide }: {
  items: Ref[]; coches: string[]; bascule: (id: string, coche: boolean) => void; vide: string;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6, maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
      {items.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{vide}</span>}
      {items.map((it) => (
        <label key={it.documentId} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, lineHeight: 1.35, cursor: 'pointer' }}>
          <input type="checkbox" checked={coches.includes(it.documentId)}
            style={{ flex: '0 0 auto', marginTop: 2 }}
            onChange={(ev) => bascule(it.documentId, ev.currentTarget.checked)} />
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {it.code}{it.nom ? ` — ${it.nom}` : ''}
          </span>
        </label>
      ))}
    </div>
  );
}

/** Création / correction d'une technique du registre.
 *
 *  Le CODE est saisi à la création puis FIGÉ : le moteur refuse de le renommer
 *  (« il ne se renomme pas — désactivez et recréez »), et offrir un champ que
 *  l'envoi rejette revient à faire perdre la saisie. */
function FormTechnique({ technique, equipements, parametres, onFerme, onEcrit }: {
  technique: { documentId: string; code: string; nom: string; principe: string | null; ordre: number | null; actif: boolean; equipements: { documentId: string }[]; analytes: { documentId: string }[] } | null;
  equipements: Ref[];
  parametres: Ref[];
  onFerme: () => void; onEcrit: () => void;
}) {
  const [code, setCode] = useState(technique?.code || '');
  const [nom, setNom] = useState(technique?.nom || '');
  const [principe, setPrincipe] = useState(technique?.principe || '');
  const [ordre, setOrdre] = useState(String(technique?.ordre ?? 0));
  const [actif, setActif] = useState(technique?.actif !== false);
  const [equipIds, setEquipIds] = useState<string[]>(technique?.equipements.map((e) => e.documentId) || []);
  const [paramIds, setParamIds] = useState<string[]>(technique?.analytes.map((a) => a.documentId) || []);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const enregistrer = () => {
    setErreur('');
    if (!nom.trim()) { setErreur('Le nom est requis.'); return; }
    if (!technique && !code.trim()) { setErreur('Le code est requis à la création.'); return; }
    setEnvoi(true);
    const corps: Record<string, unknown> = {
      nom: nom.trim(), principe: principe.trim(), ordre: Number(ordre) || 0,
      actif, equipementIds: equipIds, analyteIds: paramIds,
    };
    if (!technique) corps.code = code.trim();
    void moteur(`manuel-ref/techniques${technique ? `/${technique.documentId}` : ''}`, corps).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  /* ⚠️ UNE `Modale`, ET NON UNE CARTE POSÉE DANS LA PAGE.
     Ce formulaire était le SEUL des quarante-trois dialogues de l'application à
     se dessiner à la main — une carte au liseré cyan, glissée sous le tableau.
     Il en héritait quatre écarts visibles : un titre de 13 px là où la maison
     en pose 16/700, aucune croix de fermeture, la touche Échap sans effet, et
     un pied de boutons sans le fond creusé qui les sépare du contenu. Pire, la
     technique s'ouvrait de deux façons selon le geste : `FenetreTechnique` en
     dialogue pour la CONSULTER, cette carte pour la CORRIGER.
     Tout cela vit dans `Modale` ; il suffisait de l'employer. */
  return (
    <Modale
      titre={technique ? `Modifier ${technique.code}` : 'Déclarer une technique'}
      sousTitre="Une technique est un savoir-faire du laboratoire : elle se partage entre les analyses qui l’empruntent."
      onClose={onFerme}
      largeur={900}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme} disabled={envoi}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={enregistrer} disabled={envoi}>
            <I n="check" s={13} />{envoi ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </>
      )}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Les champs prennent `inputStyle` — la hauteur, le liseré de 1,5 px et
            l'arrondi de la maison. Ils portaient une géométrie artisanale
            (32 px de haut, sans bordure ni rayon déclarés) : dans une fenêtre
            qui montre par ailleurs des champs normaux, ils paraissaient d'une
            autre application.

            La rangée est en `flex` qui S'ENROULE, et non en trois colonnes
            fixes : 170 + 1fr + 110 tenait sur les 900 px de la fenêtre, mais
            sur un écran étroit la colonne du milieu était écrasée à zéro et
            « Nom » devenait un champ d'un caractère. Les trois champs passent
            alors l'un sous l'autre. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600, flex: '0 1 170px', minWidth: 0 }}>
            <span>Code {technique && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· figé</span>}</span>
            <input value={code} disabled={!!technique} onChange={(e) => setCode(e.currentTarget.value)}
              placeholder="ECLIA"
              style={{
                ...inputStyle, fontFamily: 'var(--font-mono)',
                // Un champ figé le DIT : fond creusé et texte adouci. Sans cela
                // il n'était gris que par le style par défaut du navigateur,
                // que `inputStyle` recouvre — il redevenait blanc, donc
                // saisissable à l'œil, alors que le moteur refuse le renommage.
                ...(technique ? { backgroundColor: 'var(--surface-sunken)', color: 'var(--text-muted)', cursor: 'not-allowed' } : null),
              }} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600, flex: '1 1 240px', minWidth: 0 }}>
            <span>Nom</span>
            <input value={nom} onChange={(e) => setNom(e.currentTarget.value)}
              placeholder="Électrochimiluminescence" style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600, flex: '0 1 110px', minWidth: 0 }}>
            <span>Ordre</span>
            <input value={ordre} onChange={(e) => setOrdre(e.currentTarget.value)} type="number"
              style={inputStyle} />
          </label>
        </div>

        <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600 }}>
          <span>Principe <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· ce qui est mesuré, et comment</span></span>
          <textarea value={principe} onChange={(e) => setPrincipe(e.currentTarget.value)} rows={3}
            placeholder="Réaction antigène-anticorps révélée par un marqueur électrochimiluminescent ; signal proportionnel à la concentration."
            // `height` retiré : `inputStyle` en pose une, qui écraserait `rows`
            // et réduirait la zone de saisie à une ligne.
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical', lineHeight: 1.5 }} />
        </label>

        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 5 }}>
            Équipements qui la réalisent
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · aucun coché = technique déclarée sans automate</span>
          </div>
          <CasesACocher items={equipements} coches={equipIds} vide="Aucun équipement au parc."
            bascule={(id, coche) => setEquipIds(coche ? [...equipIds, id] : equipIds.filter((x) => x !== id))} />
        </div>

        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 5 }}>
            Paramètres qu’elle mesure
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · une analyse composée en emploie souvent plusieurs</span>
          </div>
          <CasesACocher items={parametres} coches={paramIds} vide="Aucun paramètre au référentiel."
            bascule={(id, coche) => setParamIds(coche ? [...paramIds, id] : paramIds.filter((x) => x !== id))} />
        </div>

        {/* Désactiver et non supprimer : la technique est citée par les analyses
            qui l'emploient et par les dossiers de méthode déjà signés. */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5 }}>
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.currentTarget.checked)} />
          Technique en service
        </label>

        {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      </div>
    </Modale>
  );
}

/* ═══════════════ Les LIAISONS d'une analyse ═══════════════════════════════ */

/**
 * LE CONTRÔLE INTERNE d'une analyse — « cette analyse est-elle sous
 * contrôle ? » (onglet CQI de la fiche, RQ 08/09/2026 : « au lieu de Liaisons,
 * je veux voir CQI, et on voit si un CQI est disponible ou pas »).
 *
 * Une carte par PARAMÈTRE lié, avec ses cartes de contrôle : disponible ou
 * pas, et dans quel état. L'automate qui réalise l'analyse n'est plus ici —
 * il se lit dans l'onglet Techniques, où il est rattaché ; ne reste que celui
 * sur lequel le paramètre est RENDU (`equipementCode`), parce que c'est
 * celui que le CQI surveille.
 *
 * ── Ce que l'onglet refuse de faire ─────────────────────────────────────────
 * DEVINER LE PARAMÈTRE. Si l'analyse n'en porte aucun, il ne cherche pas de
 * ressemblance de libellé : il dit que le rattachement n'est pas fait. Un
 * rapprochement deviné afficherait un CQI qui n'est pas celui de cette
 * analyse — la pire des réponses à « suis-je sous contrôle ».
 */
export function OngletCqi({ analytes, cartesCqi }: {
  /** Les paramètres RÉELLEMENT liés à l'analyse (relation, pas devinette). */
  analytes: { documentId: string; code: string; nom: string; equipementCode?: string | null }[];
  /** Résumé des cartes de contrôle interne, déjà chargé par le magasin. */
  cartesCqi: GedCqiCarteResume[];
}) {
  const COULEUR: Record<string, { fond: string; encre: string; mot: string }> = {
    conforme: { fond: 'var(--green-50)', encre: 'var(--green-700)', mot: 'conforme' },
    alerte: { fond: 'var(--amber-50)', encre: 'var(--amber-700)', mot: 'en alerte' },
    rejet: { fond: 'var(--red-50)', encre: 'var(--red-600)', mot: 'en rejet' },
  };
  const [carteOuverte, setCarteOuverte] = useState<string | null>(null);

  if (analytes.length === 0) {
    return (
      <EmptyState
        titre="Aucun paramètre rattaché à cette analyse"
        indice="Sans ce rattachement, le contrôle interne ne peut pas être retrouvé — et cet écran ne le devine pas : un CQI deviné serait celui d’une autre analyse. Le rattachement se fait dans l’onglet Réglages." />
    );
  }
  const suivis = analytes.filter((a) => cartesCqi.some((c) => c.analyte === a.code));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* La réponse d'abord, le détail ensuite. */}
      <div role="status" style={{ fontSize: 12.5, lineHeight: 1.5, color: suivis.length === analytes.length ? 'var(--green-700)' : 'var(--amber-700)' }}>
        {suivis.length === analytes.length
          ? <>Contrôle interne disponible sur {analytes.length > 1 ? `les ${analytes.length} paramètres` : 'le paramètre'} de cette analyse.</>
          : suivis.length === 0
            ? <>Aucun contrôle interne ne suit cette analyse ({analytes.length} paramètre{analytes.length > 1 ? 's' : ''} sans carte).</>
            : <>Contrôle interne disponible sur {suivis.length} paramètre{suivis.length > 1 ? 's' : ''} sur {analytes.length} — {analytes.filter((a) => !suivis.includes(a)).map((a) => a.code).join(', ')} sans carte.</>}
      </div>
      {analytes.map((a) => {
        const cartes = cartesCqi.filter((c) => c.analyte === a.code);
        return (
          <div key={a.documentId} style={{ ...card, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.code}</span>
              <strong style={{ fontSize: 13 }}>{a.nom}</strong>
              <span style={{ marginLeft: 'auto', borderRadius: 'var(--radius-pill)', padding: '2px 10px', fontSize: 11, fontWeight: 700,
                background: cartes.length ? 'var(--green-50)' : 'var(--surface-sunken)', color: cartes.length ? 'var(--green-700)' : 'var(--text-muted)' }}>
                {cartes.length ? `CQI disponible · ${cartes.length} carte${cartes.length > 1 ? 's' : ''}` : 'pas de CQI'}
              </span>
              {a.equipementCode && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flexBasis: '100%' }}>
                  rendu sur <strong style={{ color: 'var(--text-strong)' }}>{a.equipementCode}</strong>
                </span>
              )}
            </div>
            {cartes.length === 0 ? (
              /* « Aucune carte » n'est PAS une anomalie en soi : un paramètre
                 calculé, qualitatif ou sous-traité n'en a pas. On le dit sans
                 alarmer — c'est au RQ de juger si celui-ci devrait en avoir. */
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Aucune carte ne suit ce paramètre. Ce n’est pas nécessairement un manque :
                un paramètre calculé, qualitatif ou sous-traité n’en porte pas.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 5 }}>
                {cartes.map((c) => {
                  const s = COULEUR[c.statut] || COULEUR.conforme;
                  return (
                    <button key={`${c.analyte}-${c.equipement}`} type="button"
                      onClick={() => setCarteOuverte(c.carteId)}
                      title="Ouvrir la carte CQI complète — courbe, cibles, critères de performance"
                      style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5,
                        background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
                      <span style={{ background: s.fond, color: s.encre, borderRadius: 'var(--radius-pill)', padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                        {s.mot}
                      </span>
                      <span><strong>{c.equipement}</strong>{c.equipementNom ? ` — ${c.equipementNom}` : ''}</span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {c.points} point{c.points > 1 ? 's' : ''}
                        {c.dernierPassage ? ` · dernier passage ${fmtDate(c.dernierPassage)}` : ' · aucun passage'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {carteOuverte && <DetailCarteCqi carteId={carteOuverte} onFermer={() => setCarteOuverte(null)} />}
    </div>
  );
}

/**
 * LE POP-UP D'UNE CARTE CQI (13/09/2026) — « je veux pouvoir cliquer sur la
 * fiche du CQI et un pop-up me donne toutes les infos (carte CQI, et si les
 * cartes n'appellent pas leur critère de performance, il faudra le
 * rajouter) », RQ.
 *
 * Pas de calcul ici : la courbe et les critères sont ceux déjà servis
 * ailleurs (`LeveyJennings`, `CriteresPourAnalyte`) — ce pop-up ne fait que
 * les rassembler pour UNE carte, depuis son seul `carteId`.
 */
function DetailCarteCqi({ carteId, onFermer }: { carteId: string; onFermer: () => void }) {
  type DetailCarte = {
    carte: {
      carteId: string; analyte: string; analyteNom: string; equipement: string; equipementNom: string;
      unite: string | null; nbNiveaux: number; libellesNiveaux: string[]; statut: string;
    };
    blocs: Bloc[];
  };
  const [detail, setDetail] = useState<DetailCarte | null>(null);
  const [erreur, setErreur] = useState(false);
  const { lignes: criteres, erreur: erreurCriteres } = useCriteresPerformance();

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/cqi-cartes/${carteId}?mode=lot`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (vivant) setDetail(d); })
      .catch(() => { if (vivant) setErreur(true); });
    return () => { vivant = false; };
  }, [carteId]);

  return (
    <Modale titre={detail ? `Carte CQI — ${detail.carte.analyteNom}` : 'Carte CQI'} onClose={onFermer} largeur={960}>
      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>Carte indisponible.</div>}
      {!erreur && !detail && <Skeleton rows={4} />}
      {detail && (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{detail.carte.analyte}</span>
            <span>{detail.carte.equipement}{detail.carte.equipementNom ? ` — ${detail.carte.equipementNom}` : ''}</span>
            {detail.carte.unite && <span style={{ color: 'var(--text-muted)' }}>unité {detail.carte.unite}</span>}
          </div>
          {detail.blocs.some((b) => b.points.length) ? (
            <LeveyJennings blocs={detail.blocs} nbNiveaux={detail.carte.nbNiveaux}
              onPoint={() => undefined} selection={null}
              unite={detail.carte.unite} libelles={detail.carte.libellesNiveaux || []} />
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun point sur le lot en cours.</div>
          )}
          {erreurCriteres ? (
            <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreurCriteres}</div>
          ) : !criteres ? (
            <Skeleton rows={2} />
          ) : (
            <CriteresPourAnalyte code={detail.carte.analyte} nom={detail.carte.analyteNom} lignes={criteres} />
          )}
        </div>
      )}
    </Modale>
  );
}

/* ═══════════════ LES LIAISONS D'UN PARAMÈTRE CALCULÉ ═══════════════════════ */

/* ⚠️ `FicheParametre` — la fiche paramètre en panneau, lue depuis un examen —
   a été RETIRÉE le 18/10/2026 : c'était un second dossier du même objet,
   en lecture seule, à côté du dossier complet de la liste des paramètres
   (`PanneauParametre`, ReferentielParametres.tsx). Le panneau rend
   désormais ce dossier-là (`DossierParametreLie`). Ne reste ici que
   l'éditeur des liaisons d'un paramètre calculé, que ce dossier emploie. */


/**
 * Les LIAISONS d'un paramètre — un calculé et ses composants, et rien d'autre.
 *
 * ── Pourquoi une seule sorte de liaison ─────────────────────────────────────
 *
 * « Liaison entre paramètres » recouvrait trois choses qui n'ont ni les mêmes
 * règles ni le même sens en audit : un calculé et ses composants, un paramètre
 * et son miroir sur un second automate, une équivalence entre deux codes.
 * Décision du 17/08/2026 : seule la première est modélisée. Les deux autres
 * ne sont pas « à faire plus tard » — elles sont écartées, et si elles
 * reviennent ce sera sous leur propre nom, pas sous celui-ci.
 *
 * ── Sens unique, et c'est délibéré ──────────────────────────────────────────
 *
 * Un DFG connaît la créatinine ; la créatinine ne porte pas la liste de tout
 * ce qui se calcule à partir d'elle. Cette liste grandit sans fin et n'apprend
 * rien à qui lit la créatinine.
 */
export function LiaisonsParametre({ parametre, composants, estRq, edition, setEdition, onEcrit }: {
  parametre: { documentId: string; origineValeur: string | null; formule: string | null };
  composants: { documentId: string; code: string; nom: string; origineValeur?: string | null }[];
  estRq: boolean;
  edition: boolean; setEdition: (v: boolean) => void; onEcrit: () => void;
}) {
  const calcule = parametre.origineValeur === 'calcule';
  const [tous, setTous] = useState<Ref[]>([]);
  const [choix, setChoix] = useState<string[]>(composants.map((c) => c.documentId));
  const [formule, setFormule] = useState(parametre.formule || '');
  const [enCalcul, setEnCalcul] = useState(calcule);
  const [q, setQ] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!edition) return;
    let vivant = true;
    void collection('ged-analytes', ['code', 'nom']).then((rows) => {
      if (vivant) setTous(rows.map((r) => ({ documentId: String(r.documentId), code: String(r.code), nom: String(r.nom || '') })));
    });
    return () => { vivant = false; };
  }, [edition]);

  const enregistrer = () => {
    setErreur(''); setEnvoi(true);
    void moteur(`manuel-ref/parametres/${parametre.documentId}/calcul`, {
      calcule: enCalcul,
      formule: enCalcul ? formule.trim() : '',
      composantIds: enCalcul ? choix : [],
    }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit();
    });
  };

  if (!edition) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {!calcule ? (
          <EmptyState
            titre="Paramètre mesuré — aucune liaison"
            indice="Seul un paramètre CALCULÉ dépend d’autres paramètres. Un paramètre mesuré n’a pas de composants : sa valeur vient de l’automate, pas d’une formule." />
        ) : (
          <>
            <TitreRubrique style={{ marginBottom: 0 }}>Calculé à partir de</TitreRubrique>
            {composants.length === 0 ? (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>
                Marqué calculé, mais aucun composant n’est déclaré : la formule ne s’appuie sur rien de traçable.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {composants.map((c) => (
                  <span key={c.documentId} style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-pill)', padding: '4px 12px', fontSize: 12.5 }}>
                    <strong style={{ fontFamily: 'var(--font-mono)' }}>{c.code}</strong> {c.nom}
                    {c.origineValeur === 'calcule' && <span style={{ color: 'var(--text-muted)' }}> · lui-même calculé</span>}
                  </span>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12.5 }}>
              <strong>Formule :</strong> {parametre.formule || <span style={{ color: 'var(--text-muted)' }}>non documentée</span>}
            </div>
            {/* Ce que la plateforme NE fait PAS, dit à l'endroit où on pourrait
                croire le contraire. */}
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              La formule est <strong>documentaire</strong> : elle décrit la méthode, elle n’est jamais exécutée.
              Aucun résultat patient n’est calculé ici.
            </p>
          </>
        )}
        {estRq && (
          <div><Bouton taille="sm" onClick={() => setEdition(true)}>
            {calcule ? 'Modifier la liaison' : 'Déclarer un paramètre calculé'}
          </Bouton></div>
        )}
      </div>
    );
  }

  const qn = q.trim().toLowerCase();
  const visibles = tous
    .filter((a) => a.documentId !== parametre.documentId)
    .filter((a) => !qn || `${a.code} ${a.nom}`.toLowerCase().includes(qn));

  return (
    <div style={{ display: 'grid', gap: 11 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input type="checkbox" checked={enCalcul} onChange={(e) => setEnCalcul(e.currentTarget.checked)} />
        Ce paramètre est <strong>calculé</strong> à partir d’autres paramètres
      </label>

      {enCalcul && (
        <>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, fontWeight: 600 }}>
            <span>Formule <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· documentaire, jamais exécutée</span></span>
            <input value={formule} onChange={(e) => setFormule(e.currentTarget.value)}
              placeholder="CKD-EPI 2021 · créatinine, âge, sexe" style={{ height: 32, padding: '0 8px' }} />
          </label>

          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 5 }}>
              Composants <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {choix.length} retenu(s)</span>
            </div>
            <input value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder="Filtrer les paramètres…"
              style={{ height: 30, padding: '0 8px', width: 260, marginBottom: 6 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 4, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
              {visibles.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun paramètre ne correspond.</span>}
              {visibles.map((a) => (
                <label key={a.documentId} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={choix.includes(a.documentId)}
                    onChange={(e) => setChoix(e.currentTarget.checked
                      ? [...choix, a.documentId]
                      : choix.filter((x) => x !== a.documentId))} />
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.code}{a.nom ? ` — ${a.nom}` : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {!enCalcul && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          En décochant, le paramètre redevient <strong>mesuré</strong> : sa formule et ses composants sont
          détachés. Les garder décrirait une dépendance qu’il n’a plus.
        </p>
      )}

      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: () => setEdition(false), disabled: envoi }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi, disabled: envoi, onClick: enregistrer }} />
    </div>
  );
}
